import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getUserMcpLimits, removeUserMcpLimit, saveUserMcpLimit} from 'src/openapi/admin/apis/UserMcpLimitsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {SaveContext} from 'src/hooks/use-common-data-hook';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

type Props = {
    userId: number,
};

export const useUserMcpLimits = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback((signal: AbortSignal) => apiDispatch(
        getUserMcpLimits({userId}, {signal}),
    ), [userId, apiDispatch]);

    const saveDispatchAction = useCallback(async (
        limits: Limit[],
        signal: AbortSignal,
        extra: (SaveOptions & {initialLimits?: Limit[]}) | undefined,
        {initialData, setInitialData}: SaveContext<Limit[]>,
    ) => {
        const {initialLimits, reason} = extra || {};
        if (!initialLimits) {
            return Promise.resolve({type: '', payload: {
                data: {message: 'No initial limits found'},
                success: false,
                error: true,
            } as ErrorPayload});
        }

        const removedLimits = initialData.filter(il => !limits.find(l => l.limitId === il.limitId));

        const savedLimits = initialData.filter(initial => limits.some(limit => limit.limitId === initial.limitId));
        if (!isDeepEqual({prev: savedLimits, current: limits})) {
            const saveResp = await apiDispatch(saveUserMcpLimit({
                userId,
                userLimitsSaveRequest: {limits, reason},
            }, {signal}));
            if (saveResp.payload.error) {
                return Promise.resolve({type: '', payload: saveResp.payload});
            }
            setInitialData([...limits, ...removedLimits]);
        }

        if (removedLimits.length) {
            const removeResp = await apiDispatch(removeUserMcpLimit({
                userId,
                userLimitsRemoveRequest: {limitIds: removedLimits.map(({limitId}) => limitId), reason},
            }, {signal}));
            if (removeResp.payload.error) {
                return Promise.resolve({type: '', payload: removeResp.payload});
            }
        }

        return Promise.resolve({type: '', payload: {
            data: undefined,
            success: true,
            error: false,
        } as SuccessPayload<void>});
    }, [userId, apiDispatch]);

    const hook = useCommonDataHook({
        initValue: [] as Limit[],
        getDispatchAction,
        saveDispatchAction,
    });
    const {saveChanges: save} = hook;
    const saveChanges = useCallback((initialLimits?: Limit[], options?: SaveOptions) => (
        save({initialLimits, ...options})
    ), [save]);

    return {...hook, saveChanges};
};
